import { BacksolveApproach as BacksolveApproachType } from 'common/types/valuation';
import BacksolveApproach from 'pages/Valuations/approaches/backsolveApproach/BacksolveApproach';
import createBacksolveSummaryData from 'pages/Valuations/approaches/backsolveApproach/BacksolveSummaryTable/createBacksolveSummaryData';
import createBacksolveTableData from 'pages/Valuations/approaches/backsolveApproach/BacksolveTable/createBacksolveTableData';
import { BACKSOLVE_TABLE_NAMES } from 'pages/Valuations/approaches/backsolveApproach/constants';
import createMarketAdjustmentData from 'pages/Valuations/approaches/backsolveApproach/MarketAdjustmentTable/createMarketAdjustmentData';
import createOpmInputData from 'pages/Valuations/approaches/backsolveApproach/OPMInputTable/createOpmInputData';
import createSecurityTableData from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/createSecurityTableData';
import createBacksolveSourceTableConfiguration from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/createBacksolveSourceTableConfiguration';
import normalizeSourceRowsOrder from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/normalizeSourceRowsOrder';
import {
  VALUATIONS_MUTUAL_FUND_MARKS_APPROACH,
  VALUATIONS_SECONDARY_TRANSACTIONS_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { BacksolveApproachAttributes } from 'pages/ValuationsAllocation/types';
import { EMPTY_BASKET, getApproachTableName } from 'pages/ValuationsAllocation/util';
import { getNumberValue } from 'utilities';
import { CreateBacksolveConfigProps, ExtendedValuationApproachModel } from './types';

const createBacksolveConfig = ({
  approach,
  capTableList,
  captable,
  approaches,
  backsolveAttributes,
  isDisabled,
  measurementDate,
  financials,
  financialStatementsList,
}: CreateBacksolveConfigProps) => {
  const name = getApproachTableName({ approach });
  const attrs = backsolveAttributes.backsolveApproachAttrs as BacksolveApproachAttributes;
  const backsolveAndPVAttrs = { ...backsolveAttributes.backsolveApproachAttrs, ...backsolveAttributes.shareValueAttrs };

  if (!approach.valuations_approach_backsolve?.securities_basket) {
    // eslint-disable-next-line no-param-reassign
    (approach.valuations_approach_backsolve as unknown as BacksolveApproachType).securities_basket = {
      ...EMPTY_BASKET,
    };
  }

  const backsolveSheetName = getApproachTableName({ approach, tableSuffix: BACKSOLVE_TABLE_NAMES.BACKSOLVE });

  const backsolveSheet = createBacksolveTableData({
    approach: approach as unknown as ExtendedValuationApproachModel,
    capTableList,
    attrs: backsolveAndPVAttrs,
    tableName: backsolveSheetName,
    isDisabled,
  });
  const securitySheetName = getApproachTableName({ approach, tableSuffix: BACKSOLVE_TABLE_NAMES.SECURITIES });
  const securitiesBasket = approach.valuations_approach_backsolve?.securities_basket;
  const backsolveType = approach.valuations_approach_backsolve?.backsolve_type;
  const isSourceRowsApproach
    = backsolveType === 'secondary_transactions' || backsolveType === 'mutual_fund_marks';
  const basketLength = securitiesBasket?.basket?.length ?? 0;
  // Linked (read-only target) only while the basket has a single security; multi-row
  // baskets behave like a regular backsolve and are unlinked from Source Rows.
  const isTargetValueReadOnly = isSourceRowsApproach && basketLength <= 1;
  const sourceRowsSheetName = isSourceRowsApproach
    ? getApproachTableName({ approach, tableSuffix: BACKSOLVE_TABLE_NAMES.SOURCE_ROWS })
    : undefined;
  const securitySheet = createSecurityTableData({
    captable,
    tableName: securitySheetName,
    securitiesBasket,
    isDisabled,
    isTargetValueReadOnly,
    sourceRowsSheetName,
  });

  const opmTableName = getApproachTableName({ approach, tableSuffix: BACKSOLVE_TABLE_NAMES.OPM_INPUTS });
  const opmInputSheet = createOpmInputData({
    attrs,
    tableName: opmTableName,
    data: approach.valuations_approach_backsolve,
    measurementDate,
    approaches,
    isDisabled,
  });

  const constantsFinancialStatementIds = (financialStatementsList ?? [])
    .map(financialStatement => getNumberValue(financialStatement?.id))
    .filter(Boolean);

  const backsolveSummarySheet = createBacksolveSummaryData({
    approach,
    attrs,
    isDisabled,
    financials,
    constantsFinancialStatementIds,
  });

  const marketAdjustmentSheet = createMarketAdjustmentData({
    approach: approach as unknown as ExtendedValuationApproachModel,
    approaches,
    backsolveSummarySheet,
    attrs: attrs!,
    isDisabled,
  });

  const sourceRowsTableTitle
    = backsolveType === 'mutual_fund_marks'
      ? VALUATIONS_MUTUAL_FUND_MARKS_APPROACH
      : VALUATIONS_SECONDARY_TRANSACTIONS_APPROACH;

  const sourceRowsSheet = isSourceRowsApproach
    ? createBacksolveSourceTableConfiguration({
      rows: normalizeSourceRowsOrder(approach.valuations_approach_backsolve?.source_rows ?? []),
      isDisabled,
      name: sourceRowsSheetName!,
      tableTitle: sourceRowsTableTitle,
      fieldAttributes: attrs,
    })
    : undefined;

  return {
    name,
    approach,
    value: approach.enterprise_value,
    spreadsheets: {
      backsolveSheet,
      securitySheet,
      ...(sourceRowsSheet ? { sourceRowsSheet } : {}),
      backsolveSummarySheet,
      marketAdjustmentSheet,
      opmInputSheet,
    },
    enterpriseValueReference: marketAdjustmentSheet.name,
    component: BacksolveApproach,
  };
};

export default createBacksolveConfig;
